/**
 * OraConcierge — proactive floating-card presence.
 *
 * Per Commander directive 2026-06-10: ORA was "virtually invisible — minimal,
 * reduced, quiet." She was a thin top-right text affordance ("Ask ORA")
 * with no proactive surfacing and no upgrade-driving recs. That's wrong
 * for the host role. This component fixes it without replacing OraTopBar
 * (which remains the canonical summon point in the chrome).
 *
 * Behavior:
 *   - Renders a dismissible floating card bottom-right
 *   - Appears 4.5s after page mount (gives the page time to settle so
 *     ORA doesn't shout over the first paint)
 *   - Pulls a context-aware recommendation from /api/oracle/recommend
 *     using the current pathname as the `surface` hint
 *   - Free / anonymous users: signup nudge with Pro-feature teaser
 *   - Pro users: next-best-action from /recommend (skill they haven't used)
 *   - Dismissible per-session (sessionStorage) so she doesn't pester
 *     within a single visit, BUT comes back on the NEXT page load —
 *     ORA is a host, not a tab-pinned puppy
 *   - 60s idle re-tap: if the user stays on the same page idle for 60s
 *     after dismissing, ORA wakes once more with a different rec
 *
 * Visual design: gold-bordered charcoal card matching the brand. No
 * bubble, no avatar, no "is-typing" animation. Two lines of body + one
 * primary CTA + dismiss X. Cooperative, not pushy.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight, Sparkles } from "lucide-react";
import api from "@/utils/api";

// How long ORA waits after page mount before surfacing. Tunable here
// rather than hardcoded so we can adjust based on user-research data.
const INITIAL_DELAY_MS = 4500;
const IDLE_REWAKE_MS = 60000;

// Per-pathname dismissal key — dismissing on /dashboard doesn't silence
// her on /subscribe. Stored in sessionStorage so each browser session
// gets a clean slate (we want her presence across multi-day return
// visits; "permanent dismiss" lives in user prefs, not here).
function dismissKey(pathname) {
  return `ora-concierge-dismissed:${pathname}`;
}

export default function OraConcierge({ theme, isAuthenticated, tier = "free" }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [visible, setVisible] = useState(false);
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const idleTimerRef = useRef(null);

  // ─── Fetch a recommendation appropriate to this surface ─────────────────
  // Backend shape: {id, kind, label, body, cta_text, cta_path, promised_outcome}
  // Static-fallback shape: {id, title, body, cta_label, href}
  // We normalize to {id, title, body, cta_label, href} on ingest so the render
  // path reads ONE field set regardless of source. 2026-06-13 incident showed
  // the GO button was a no-op for backend-sourced recs because the renderer
  // was reading `href` while the backend was writing `cta_path`.
  const normalizeRec = (r) => {
    if (!r) return r;
    return {
      ...r,
      title: r.title || r.label || r.headline || "What's next?",
      body: r.body || r.description || "I have a suggestion for you.",
      cta_label: r.cta_label || r.cta_text || "Go",
      href: r.href || r.cta_path || r.url || "",
    };
  };

  const fetchRec = useCallback(async () => {
    setLoading(true);
    try {
      const surface = pathnameToSurface(pathname);
      const resp = await api.post(
        '/oracle/recommend',
        { surface, concierge: true },
        { timeout: 6000 },
      );
      const primary = resp.data?.recommendations?.primary;
      if (primary) setRec(normalizeRec(primary));
      else setRec(normalizeRec(fallbackRec(pathname, isAuthenticated, tier)));
    } catch (e) {
      // Honest degrade: if the backend can't reach us in 6s, ORA still
      // shows a static rec. She never blocks on the network.
      setRec(normalizeRec(fallbackRec(pathname, isAuthenticated, tier)));
    } finally {
      setLoading(false);
    }
  }, [pathname, isAuthenticated, tier]);

  // ─── Initial surface (4.5s after mount) ─────────────────────────────────
  useEffect(() => {
    // Respect prior dismissal within the same session
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (window.sessionStorage.getItem(dismissKey(pathname))) return;
    }
    const t = setTimeout(() => {
      setVisible(true);
      fetchRec();
    }, INITIAL_DELAY_MS);
    return () => clearTimeout(t);
  }, [pathname, fetchRec]);

  // ─── Idle re-tap: after dismissal, wake again at IDLE_REWAKE_MS ─────────
  const handleDismiss = useCallback(() => {
    setVisible(false);
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.setItem(dismissKey(pathname), String(Date.now()));
    }
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // Wake with a fresh rec — different surface hint so she suggests
      // something new the second time.
      setVisible(true);
      fetchRec();
    }, IDLE_REWAKE_MS);
  }, [pathname, fetchRec]);

  useEffect(() => {
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  if (!visible || !rec) return null;

  const palette = {
    bg: theme?.cardBg || "#14130F",
    border: theme?.primary || "#C9A227",
    text: theme?.textPrimary || "#F5F5F0",
    sub: theme?.textSecondary || "rgba(245,245,240,0.65)",
    accent: theme?.primary || "#C9A227",
  };

  const handleCta = () => {
    // Telemetry — best-effort, never blocks navigation
    try {
      api.post(
        '/oracle/telemetry',
        {
          event: "concierge_cta_click",
          rec_id: rec.id || rec.kind || "unknown",
          surface: pathnameToSurface(pathname),
        },
        { timeout: 2000 },
      ).catch(() => {});
    } catch {}
    if (rec.href) navigate(rec.href);
  };

  return (
    <aside
      data-testid="ora-concierge"
      role="complementary"
      aria-label="ORA — recommendation"
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 50,
        maxWidth: 360,
        width: "calc(100vw - 40px)",
        background: palette.bg,
        border: `1px solid ${palette.border}55`,
        borderLeft: `3px solid ${palette.accent}`,
        borderRadius: 2,
        padding: "16px 18px 14px 18px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.4), 0 2px 6px rgba(201,162,39,0.08)",
        fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
        animation: "ora-slide-in 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      {/* Inject keyframes once via a style tag — cheaper than a CSS file */}
      <style>{`
        @keyframes ora-slide-in {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss ORA"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "transparent",
          border: "none",
          color: palette.sub,
          cursor: "pointer",
          padding: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <X size={14} />
      </button>
      <div style={{
        fontSize: 10,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: palette.accent,
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}>
        <Sparkles size={11} /> ORA · Host
      </div>
      <h3 style={{
        fontSize: 15,
        fontWeight: 600,
        color: palette.text,
        margin: "0 0 6px 0",
        letterSpacing: "-0.005em",
      }}>
        {rec.title || "What's next?"}
      </h3>
      <p style={{
        fontSize: 12.5,
        lineHeight: 1.5,
        color: palette.sub,
        margin: "0 0 14px 0",
      }}>
        {rec.body || rec.description || "I have a suggestion for you."}
      </p>
      <button
        onClick={handleCta}
        data-testid="ora-concierge-cta"
        style={{
          background: palette.accent,
          color: "#0A0A0B",
          border: "none",
          padding: "8px 14px",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          borderRadius: 2,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontFamily: "inherit",
        }}
      >
        {rec.cta_label || "Go"} <ArrowRight size={12} />
      </button>
    </aside>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function pathnameToSurface(pathname) {
  if (!pathname || pathname === "/") return "landing";
  if (pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname.startsWith("/subscribe")) return "subscribe";
  if (pathname.startsWith("/ai")) return "ai_assistant";
  if (pathname.startsWith("/brief")) return "brief";
  if (pathname.startsWith("/login") || pathname.startsWith("/register")) return "auth";
  return "general";
}

// Static fallbacks for when the backend can't be reached. Conservative —
// always points at the highest-leverage skill for the (auth, tier, surface)
// triple. NEVER fabricates market opinions; only navigates within the
// product surface. Honors ORA's "never speak Warren's lane" rule.
function fallbackRec(pathname, isAuthenticated, tier) {
  const surface = pathnameToSurface(pathname);
  // Anonymous → push them to free signup
  if (!isAuthenticated) {
    if (surface === "auth") {
      return {
        id: "fallback_auth_back_to_dashboard",
        title: "Already have an account?",
        body: "Pick up where you left off. Your council and watches are still waiting.",
        cta_label: "Log in",
        href: "/login",
      };
    }
    return {
      id: "fallback_anon_signup",
      title: "Take Gamma QC for a test drive",
      body: "Free account, no card. Get Warren's voiced verdicts, your Trader DNA, and a 7-day Pro trial when you want to go deeper.",
      cta_label: "Start free",
      href: "/register",
    };
  }
  // Free tier → surface the Pro skills they haven't tried.
  // Note: backend never returns tier="trial" — trial state lives in
  // subscription_status while tier stays "pro" for the duration. The
  // "trial" check is kept defensively in case a legacy code path
  // bypasses the auth/me normalization, but the canonical signal is
  // tier === "free".
  if (tier === "free" || tier === "trial" || !tier) {
    if (surface === "dashboard") {
      return {
        id: "fallback_free_to_pro",
        title: "Unlock the Sealed Cockpit",
        body: "Algorithmic Hedge Strategy, 10-seat Council, Ed25519-signed receipts. 7-day trial, no card up front.",
        cta_label: "See Pro",
        href: "/subscribe",
      };
    }
    if (surface === "subscribe") {
      return {
        id: "fallback_free_on_subscribe",
        title: "Pick the right tier",
        body: "Retail ($49/mo) covers most active traders. Inst ($12k/mo) adds the Council and a signed-receipt compliance pack.",
        cta_label: "Compare",
        href: "/subscribe",
      };
    }
    return {
      id: "fallback_free_dashboard",
      title: "Open your dashboard",
      body: "Your Trader DNA, Warren's verdicts, and your watch list are one click away.",
      cta_label: "Dashboard",
      href: "/dashboard",
    };
  }
  // Pro → surface deep features
  return {
    id: "fallback_pro_deep",
    title: "Run a Sacred Decision",
    body: "Hand Warren a ticker and the 10-seat Council audits the verdict before you trade.",
    cta_label: "Open Warren",
    href: "/ai",
  };
}
